import { Stack } from "@mui/material";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import { observer } from "mobx-react";
import React from "react";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { useStore } from "utils/useStore";
import AppliedFilterChip from "../BaseChipComponents/AppliedFilterChipBase";
import { IAppliedFilterChipComponent } from "../BaseChipComponents/IAppliedFilterChipComponent";
import {
    CHIP_AGGREGATION_THRESHOLD,
    renderCollectionChips,
    useChipFiltersStore,
} from "../BaseChipComponents/chipUtils";

export const AgentChips = observer(function (
    props: IAppliedFilterChipComponent,
) {
    const applicationFiltersStore = useChipFiltersStore(props);
    const applicationFilterValuesStore = useStore(ApplicationFilterValuesStore);

    const isAgentLoading = applicationFilterValuesStore.getTaskLoading(
        ApplicationFilterValuesStore.Tasks.LOAD_AGENTS,
    );

    const agentIds = applicationFiltersStore.agentIds;
    const agentChipCount = agentIds.length;
    const shouldGroupAgents = agentChipCount > CHIP_AGGREGATION_THRESHOLD;
    const getAgentLabel = (agentId: string) =>
        applicationFilterValuesStore.agents.find((a) => a.id === agentId)
            ?.fullName ?? agentId;

    const agentChipLoadingIndicator = (
        <Stack
            direction="row"
            flexWrap="nowrap"
            alignItems="center"
            spacing={1}
        >
            <span>Agent:</span>
            <AcxLoadingIndicator size={16} alternate="PuffLoader" />
        </Stack>
    );

    if (isAgentLoading) {
        if (shouldGroupAgents) {
            return (
                <AppliedFilterChip
                    disabled={props.disabled}
                    isApplicable={props.isApplicable}
                >
                    {agentChipLoadingIndicator}
                </AppliedFilterChip>
            );
        }
        return agentIds.map((agentId) => (
            <AppliedFilterChip
                disabled={props.disabled}
                isApplicable={props.isApplicable}
                key={agentId}
            >
                {agentChipLoadingIndicator}
            </AppliedFilterChip>
        ));
    }

    return renderCollectionChips({
        items: agentIds,
        disabled: props.disabled,
        isApplicable: props.isApplicable,
        getKey: (agentId) => agentId,
        getLabel: (agentId) => `Agent: ${getAgentLabel(agentId)}`,
        getTooltipLabel: getAgentLabel,
        getAggregatedLabel: (count) => `${count} Agents`,
        onRemoveAll: () => applicationFiltersStore.setAgentIds([]),
        onRemoveItem: (agentId) =>
            applicationFiltersStore.setAgentIds(
                applicationFiltersStore.agentIds.filter(
                    (selectedAgentId) => selectedAgentId !== agentId,
                ),
            ),
    });
});
